import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, ArrowLeft, Eye, EyeOff, Check, ChevronsUpDown } from 'lucide-react';
import { clientService } from '@/services/client.service';
import { franchiseService } from '@/services/franchise.service';
import { telegramLogService } from '@/services/telegram-log.service';
import { loginService } from '@/services/login.service';
import { multiNivelService } from '@/services/multi-nivel.service';
import { Util } from '@/utils/utils';
import { useSpinner } from '@/hooks/useSpinner';
import { useToast } from '@/hooks/use-toast';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import { cn } from '@/lib/utils';
import { debounce } from 'lodash';
import { navigateToRoute } from '@/utils/environment-urls';

// Schema de validação
const clientSchema = z.object({
  nome_razao_social: z.string().min(1, 'Nome é obrigatório').refine((val) => val.includes(' '), {
    message: 'Por favor, insira um sobrenome'
  }),
  cpf_cnpj: z.string().min(14, 'CPF é obrigatório'),
  email: z.string().email('E-mail inválido'),
  telefone: z.string().min(14, 'Telefone é obrigatório'),
  estado: z.string().min(1, 'Estado é obrigatório'),
  cidade: z.string().min(1, 'Cidade é obrigatória'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Você deve aceitar os termos para continuar',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type ClientFormData = z.infer<typeof clientSchema>;

const CadastroCliente = () => {
  const { indicador } = useParams<{ indicador?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading, showSpinner, hideSpinner } = useSpinner();

  // Estados do componente
  const [franquiasDisponiveis, setFranquiasDisponiveis] = useState<any[]>([]);
  const [franquiaSelecionada, setFranquiaSelecionada] = useState<string>('');
  const [franchiseSubdomain] = useState<string>(getSubdomainFranchise());
  const [showDocumentCheck, setShowDocumentCheck] = useState(true);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [documentPreRegister, setDocumentPreRegister] = useState('');
  const [cidades, setCidades] = useState<string[]>([]);
  const [openCidade, setOpenCidade] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Estados para código de indicação
  const [showCodigoIndicacao, setShowCodigoIndicacao] = useState(false);
  const [codigoIndicacao, setCodigoIndicacao] = useState('');
  const [nomeIndicante, setNomeIndicante] = useState('');
  const [codigoIndicacaoValido, setCodigoIndicacaoValido] = useState<boolean | null>(null);
  const [indicadorFromCode, setIndicadorFromCode] = useState<string>('');

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    control
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    mode: 'onChange',
    defaultValues: {
      acceptTerms: false
    }
  });

  const watchEstado = watch('estado');

  // Carregar franquias disponíveis
  useEffect(() => {
    const loadFranquias = async () => {
      showSpinner('Carregando franquias...', 'Aguarde, carregando informações das franquias');
      try {
        const franquias = await franchiseService.findAllAvailable();
        setFranquiasDisponiveis(franquias);        
        // Selecionar automaticamente a franquia baseada no subdomínio
        setFranquiaSelecionada(franchiseSubdomain);        
      } catch (error) {
        console.error('Erro ao carregar franquias:', error);
        toast({
          title: 'Erro',
          description: 'Erro ao carregar franquias disponíveis',
          variant: 'destructive'
        });
      } finally {
        hideSpinner();
      }
    };

    loadFranquias();
  }, []); // Removendo as dependências que causavam o loop

  // Atualizar cidades quando estado mudar
  useEffect(() => {
    if (watchEstado) {
      const estadoSelecionado = Util.estados.find(estado => estado.sigla === watchEstado);
      if (estadoSelecionado) {
        setCidades(estadoSelecionado.cidades);
        setValue('cidade', ''); // Limpar cidade selecionada
      }
    }
  }, [watchEstado, setValue]);

  // Funções de máscara
  const aplicarMascaraCPF = (value: string) => {
    const numero = value.replace(/\D/g, '');
    
    if (numero.length <= 11) {
      // CPF: 000.000.000-00
      return numero.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
  };

  const aplicarMascaraTelefone = (value: string) => {
    const numero = value.replace(/\D/g, '');
    return numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Verificar código de indicação
  const verificarCodigoIndicacao = async (codigo: string) => {
    if (!codigo || codigo.length === 0) {
      setCodigoIndicacaoValido(null);
      setNomeIndicante('');
      setIndicadorFromCode('');
      return;
    }

    try {
      const response = await multiNivelService.getIndicantePorCodigo(codigo);
      
      if (response.success && response.data) {
        setCodigoIndicacaoValido(true);
        setNomeIndicante(response.data.nome);
        setIndicadorFromCode(codigo);
        toast({
          title: 'Código válido!',
          description: `Indicação por: ${response.data.nome}`,
          variant: 'default',
          duration: 3000
        });
      } else {
        setCodigoIndicacaoValido(false);
        setNomeIndicante('');
        setIndicadorFromCode('');
        toast({
          title: 'Código inválido',
          description: response.message || 'Código de indicação não encontrado',
          variant: 'destructive',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      setCodigoIndicacaoValido(false);
      setNomeIndicante('');
      setIndicadorFromCode('');
      toast({
        title: 'Erro',
        description: 'Erro ao verificar código de indicação',
        variant: 'destructive',
        duration: 3000
      });
    }
  };

  // Versão debounced da verificação de código
  const verificarCodigoIndicacaoDebounced = debounce(verificarCodigoIndicacao, 800);

  // Verificar pré-cadastro
  const checkPreRegister = async () => {
    if (!documentPreRegister || documentPreRegister.length < 14) {
      toast({
        title: 'Atenção',
        description: 'Informe um CPF válido',
        variant: 'destructive'
      });
      return;
    }

    if (!franquiaSelecionada) {
      toast({
        title: 'Atenção',
        description: 'Selecione uma franquia',
        variant: 'destructive'
      });
      return;
    }

    showSpinner('Validando CPF...', 'Verificando se o documento já está cadastrado');
    
    try {
      const response = await clientService.checkCpf(documentPreRegister);
      
      if (!response.success) {
        // CPF já cadastrado
        if (response.error === 'cpf_revendedora') {
          toast({
            title: 'CPF já cadastrado!',
            description: 'Já está registrado como revendedora.',
            variant: 'default',
            duration: 8000
          });
        } else if (response.error === 'cpf_cliente') {
          const emailMascarado = response.data?.email || '';
          toast({
            title: 'CPF já cadastrado!',
            description: `Este CPF já está registrado como cliente. Email: ${emailMascarado}`,
            duration: 8000
          });
        } else {
          toast({
            title: 'Erro na validação',
            description: typeof response.error === 'string' ? response.error : 'Erro ao validar CPF',
            variant: 'destructive'
          });
        }
        
        hideSpinner();
        return;
      }

      // CPF disponível - pode prosseguir com o cadastro
      const clientData = response.data;
      
      // Preencher dados se houver histórico de clientes
      if (clientData?.clientes?.length > 0) {
        const cliente = clientData.clientes[0];
        setValue('nome_razao_social', cliente.nome_cliente.replace("  "," "));
        setValue('telefone', aplicarMascaraTelefone(cliente.telefone_cliente));
        
        toast({
          title: 'Olá novamente!',
          description: 'Encontramos alguns dos seus dados. Verifique e finalize seu cadastro.',
          variant: 'success',
          duration: 5000
        });
      }

      setValue('cpf_cnpj', documentPreRegister);
      setShowDocumentCheck(false);
      setShowRegisterForm(true);

    } catch (error: any) {      
      hideSpinner();
      console.error('Erro ao verificar CPF:', error);
      toast({
        title: 'Erro na validação',
        description: error.message || 'Erro ao validar CPF. Tente novamente.',
        variant: 'destructive'
      });
    }
    
    hideSpinner();
  };

  // Submeter formulário
  const onSubmit = async (data: ClientFormData) => {
    showSpinner('Realizando cadastro...', 'Criando sua conta, aguarde um momento');

    try {
      // Remover confirmPassword e acceptTerms dos dados enviados para a API
      const { confirmPassword, acceptTerms, ...clientData } = data;
      
      // Usar código de indicação se foi informado, senão usar o parâmetro da URL
      const codigoIndicador = indicadorFromCode || indicador;
      const response = await clientService.createClient(clientData, codigoIndicador);

      if (response.error) {
        throw new Error(response.error.message || 'Erro ao cadastrar cliente');
      }

      // Log no Telegram enviado pelo servidor

      toast({
        title: 'Cadastro realizado!',
        description: 'Aguarde...',
        duration: 1500,
        variant: 'default'
      });

      // Fazer login automático
      showSpinner('Fazendo login...', 'Aguarde, entrando em sua conta');
      
      const loginResponse = await loginService.checkusernameandpassword(
        data.email.trim().toLowerCase(), 
        data.password, 
        franquiaSelecionada
      );
      if (loginResponse.userData && loginResponse.idToken && loginResponse.refreshToken) {
        // Salvar dados de autenticação
        localStorage.setItem('token', loginResponse.idToken);
        localStorage.setItem('refreshToken', loginResponse.refreshToken);
        localStorage.setItem('userlogged', JSON.stringify(loginResponse.userData));
        
        // Salvar nome da franquia se disponível
        const nomeFranquia = franquiasDisponiveis.find((franquia) => franquia.subdomain === franquiaSelecionada)?.name || null;
        if (nomeFranquia) {
          localStorage.setItem('franquia', nomeFranquia);
        }
        // Redirecionar para área do cliente
        navigateToRoute('/home-cliente', true);
      } else {
        // Se falhar o login automático, redirecionar para tela de login
        toast({
          title: 'Cadastro realizado!',
          description: 'Faça login para acessar sua conta.',
          variant: 'default'
        });        
       
        navigate('/authentication/side-login');
 
      }

    } catch (error: any) {
      console.error('Erro ao cadastrar:', error);
      
      // Log de erro no Telegram
      await telegramLogService.send(
        false,
        `❌ ERRO NO CADASTRO DE CLIENTE\n\nErro: ${error.message}\nDados: ${JSON.stringify(data, null, 2)}`,
        new Date(),
        null
      );

      toast({
        title: 'Erro',
        description: error.message || 'Erro ao realizar cadastro. Tente novamente.',
        variant: 'destructive'
      });
    }

    hideSpinner();
  };

  const onFranquiaChange = (franquia: string) => {
    showSpinner('Redirecionando...', 'Aguarde, redirecionando para a franquia selecionada');
    
    // Determinar se estamos em produção ou desenvolvimento
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    
    const urlFranquia = isProduction
      ? `https://${franquia}.xdoinfinitto.com.br/cadastro-cliente`
      : `http://${franquia}.localhost:3000/cadastro-cliente`;
      
    const urlMatriz = isProduction
      ? `https://xdoinfinitto.com.br/cadastro-cliente`
      : `http://localhost:3000/cadastro-cliente`;
    
    if (franquia === 'matriz') {
      window.open(urlMatriz, '_self');
    } else {
      window.open(urlFranquia, '_self');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/v2/logos/logo-horizontal.png" 
                alt="Logo" 
                className="h-12"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <CardTitle className="text-2xl font-bold">Cadastro de Cliente</CardTitle>
            <CardDescription>
              {/* {indicador && (
                <div className="text-sm text-blue-600 mb-2">
                  Indicado por: Código {indicador}
                </div>
              )} */}
              Preencha seus dados para se cadastrar
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Seleção de Franquia */}
            <div className="space-y-2">
              <Label htmlFor="franquia">Selecione sua Franquia</Label>
              <Select 
                value={franquiaSelecionada} 
                onValueChange={(value) => {
                  if (value !== franchiseSubdomain) {
                    onFranquiaChange(value);
                  } else {
                    setFranquiaSelecionada(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione sua franquia" />
                </SelectTrigger>
                <SelectContent>
                  {franquiasDisponiveis.map((franquia) => (
                    <SelectItem key={franquia.subdomain} value={franquia.subdomain}>
                      {franquia.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Verificação de Documento */}
            {showDocumentCheck && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="document">CPF</Label>
                  <Input
                    id="document"
                    value={documentPreRegister}
                    onChange={(e) => setDocumentPreRegister(aplicarMascaraCPF(e.target.value))}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>
                <Button 
                  onClick={checkPreRegister}
                  disabled={isLoading || !franquiaSelecionada}
                  className="w-full"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Continuar
                </Button>
              </div>
            )}

            {/* Formulário de Cadastro */}
            {showRegisterForm && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_razao_social">Nome</Label>
                  <Input
                    id="nome_razao_social"
                    {...register('nome_razao_social')}
                    placeholder="Nome completo ou razão social"
                  />
                  {errors.nome_razao_social && (
                    <p className="text-sm text-red-500">{errors.nome_razao_social.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf_cnpj">CPF</Label>
                  <Input
                    id="cpf_cnpj"
                    {...register('cpf_cnpj')}
                    readOnly
                    className="bg-gray-50"
                    maxLength={14}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone/WhatsApp</Label>
                  <Input
                    id="telefone"
                    {...register('telefone')}
                    onChange={(e) => setValue('telefone', aplicarMascaraTelefone(e.target.value))}
                    placeholder="(99) 99999-9999"
                    maxLength={15}
                  />
                  {errors.telefone && (
                    <p className="text-sm text-red-500">{errors.telefone.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    placeholder="Digite seu melhor email"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email.message}</p>
                  )}
                  <p className="text-xs text-gray-600">
                    Seu melhor e-mail! Será usado para acessar e gerenciar sua conta.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Crie sua senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      placeholder="*********"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">{errors.password.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirme sua senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirmPassword')}
                      placeholder="*********"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
                  )}
                </div>
                <div>
                  
                {/* Link para código de indicação */}
                {!indicador && !showCodigoIndicacao && (
                  <div className="text-center">
                    <Button
                      variant="link"
                      className="text-sm text-blue-600 hover:text-blue-800 p-0 h-auto"
                      onClick={() => setShowCodigoIndicacao(true)}
                    >
                      Possui um código de indicação? Clique aqui
                    </Button>
                  </div>
                )}

                {/* Campo para código de indicação */}
                {showCodigoIndicacao && (
                  <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label htmlFor="codigoIndicacao">Código de Indicação</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="codigoIndicacao"
                          type='number'
                          value={codigoIndicacao}
                          onChange={(e) => {
                              const valor = e.target.value;
                              setCodigoIndicacao(valor.replace(/\s/g, ''));
                          }}
                          placeholder="Digite o código de indicação"
                          className={cn(
                            codigoIndicacaoValido === true && "border-green-500",
                            codigoIndicacaoValido === false && "border-red-500"
                          )}
                        />
                        {codigoIndicacaoValido === true && (
                          <CheckCircle className="absolute right-3 top-3 w-4 h-4 text-green-500" />
                        )}
                        {codigoIndicacaoValido === false && (
                          <XCircle className="absolute right-3 top-3 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-gray-500 px-3"
                        size="sm"
                        onClick={() => {
                          if (codigoIndicacao.length >= 3) {
                            verificarCodigoIndicacao(codigoIndicacao.replace(/\s/g, ''));
                          } else {
                            toast({
                              title: 'Código muito curto',
                              description: 'Digite pelo menos 3 caracteres para verificar',
                              variant: 'destructive',
                              duration: 2000
                            });
                          }
                        }}
                        disabled={codigoIndicacao.length < 3}
                      >
                        Verificar
                      </Button>
                    </div>
                    {nomeIndicante && (
                      <p className="text-sm text-green-600">✓ Indicação por: {nomeIndicante}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowCodigoIndicacao(false);
                          setCodigoIndicacao('');
                          setCodigoIndicacaoValido(null);
                          setNomeIndicante('');
                          setIndicadorFromCode('');
                        }}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      {codigoIndicacaoValido === true && (
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() => setShowCodigoIndicacao(false)}
                          className="flex-1"
                        >
                          Confirmar
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado</Label>
                    <Select onValueChange={(value) => setValue('estado', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {Util.estados.map((estado) => (
                          <SelectItem key={estado.sigla} value={estado.sigla}>
                            {estado.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.estado && (
                      <p className="text-sm text-red-500">{errors.estado.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade</Label>
                    {watchEstado ? (
                      <Popover open={openCidade} onOpenChange={setOpenCidade}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openCidade}
                            className="w-full justify-between"
                          >
                            {watch('cidade') || "Selecione a cidade..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Buscar cidade..." />
                            <CommandList>
                              <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                              <CommandGroup>
                                {cidades.map((cidade) => (
                                  <CommandItem
                                    key={cidade}
                                    value={cidade}
                                    onSelect={(currentValue) => {
                                      setValue('cidade', currentValue === watch('cidade') ? '' : currentValue);
                                      setOpenCidade(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        watch('cidade') === cidade ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {cidade}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Button variant="outline" disabled className="w-full justify-start">
                        Selecione estado
                      </Button>
                    )}
                    {errors.cidade && (
                      <p className="text-sm text-red-500">{errors.cidade.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 pt-4">
                  <div className="flex items-start space-x-2">
                    <Controller
                      name="acceptTerms"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="acceptTerms"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1"
                        />
                      )}
                    />
                    <Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
                      Aceito os{' '}
                      <a 
                        href="https://xdoinfinitto.com.br/assets/Termos de Uso.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        termos de uso,
                      </a>
                      {' '}termos do{' '}
                      <a 
                        href="https://xdoinfinitto.com.br/assets/Termos de Uso e Regulamento - Programa de Benefícios.pdf" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        programa de benefícios,
                      </a>
                      {' '}e termos do{' '}
                      <a 
                        href="https://xdoinfinitto.com.br/assets/Termos e Regulamento Programa de Indicacao e Rede.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        programa de indicação e rede
                      </a>
                    </Label>
                  </div>
                  {errors.acceptTerms && (
                    <p className="text-sm text-red-500">{errors.acceptTerms.message}</p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? 'Cadastrando...' : 'Cadastrar-se'}
                </Button>
              </form>         
            )}
            {nomeIndicante && (
              <div>
                <p className="text-sm text-green-300">Indicação por: {nomeIndicante}</p>
              </div>
            )}          

            <div className="text-center text-sm">
              Já tem uma conta?{' '}
              <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/authentication/side-login')}>
                Entrar
              </Button>
            </div>

            {showRegisterForm && (
              <>
                <hr className='my-2'/>
                <p className='text-muted-foreground text-xs'>Lembre-se: esta é uma conta de cliente, onde não é possível fazer vendas, apenas acompanhar compras e benefícios. Para vendas, cadastra-se <a href="/v2/authentication/side-register" className="text-blue-600 hover:text-blue-800 underline">aqui</a></p>
              
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDocumentCheck(true);
                    setShowRegisterForm(false);
                    setDocumentPreRegister('');
                    reset();
                  }}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CadastroCliente;
